// External Imports
import { useContext, useState } from "react";
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import { MdClose } from "react-icons/md";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import { DataContext } from "../../context/data-context";
import LoadingSpinner from "../UI/LoadingSpinner";
import { requestAdminAPI } from "../../api/posh-api";

// Internal Imports

/** ========================================================================
 * * PrintLabelModal Component
 * This component renders a modal for printing shipping labels.
 * ========================================================================== */

export default function PrintLabelModal({
  open,
  setOpen,
  recipient,
  refresh,
  repossessionId,
}) {
  const { admin } = useContext(DataContext);
  const [loading, setLoading] = useState(false);

  /* ====================== CONFIRM HANDLER ====================== */
  const confirmHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        name: document.getElementById("name")?.value || "",
        fromName: document.getElementById("fromName")?.value || "",
        fromStreet: document.getElementById("fromStreet")?.value || "",
        fromCity: document.getElementById("fromCity")?.value || "",
        fromState: document.getElementById("fromState")?.value || "",
        fromZip: document.getElementById("fromZip")?.value || "",
        fromEmail: document.getElementById("deliveryEmail")?.value || "",
        toStreet: document.getElementById("toStreet")?.value || "",
        toCity: document.getElementById("toCity")?.value || "",
        toState: document.getElementById("toState")?.value || "",
        toZip: document.getElementById("toZip")?.value || "",
        labelSize: document.getElementById("labelSize")?.value || "1",
        customerId: recipient?.id,
      };
      const response = await requestAdminAPI("get-mail-label", {
        data: payload,
        repossessionId: repossessionId,
      });
      window.open(response, "_blank");
      refresh && refresh();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };
  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal">
        <Heading
          title="Print Shipping Label"
          text="Submitting this form will create a USPS shipping label. Please note that each label costs up to $11."
          isBackArrow={false}
        >
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>
        <p className="text-sm font-semibold text-yellow-600">
          Recipient Information
        </p>
        <div className="info">
          <div className="lg:col-span-2">
            <DetailPair
              label="Name"
              edit={true}
              loading={loading}
              inputOptions={{
                id: "name",
                defaultValue: recipient?.firstName
                  ? `${recipient.firstName} ${recipient.lastName}`
                  : "",
              }}
            />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              label="Street Address"
              edit={true}
              loading={loading}
              inputOptions={{
                id: "toStreet",
                defaultValue: recipient?.street?.toUpperCase() || "",
              }}
            />
          </div>
          <DetailPair
            label="City"
            edit={true}
            loading={loading}
            inputOptions={{
              id: "toCity",
              defaultValue: recipient?.city || "",
            }}
          />
          <DetailPair
            label="State"
            edit={true}
            loading={loading}
            inputOptions={{
              id: "toState",
              defaultValue: recipient?.state || "",
              maxLength: 2,
            }}
          />
          <DetailPair
            label="Zip Code"
            edit={true}
            loading={loading}
            inputOptions={{
              id: "toZip",
              defaultValue: recipient?.zip || "",
              maxLength: 5,
            }}
          />
        </div>
        <p className="text-sm font-semibold text-yellow-600">
          Sender Information
        </p>
        <div className="info">
          <div className="lg:col-span-2">
            <DetailPair
              label="Name"
              edit={true}
              loading={loading}
              inputOptions={{
                id: "fromName",
                defaultValue: "Repossession Department",
              }}
            />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              label="Street Address"
              edit={true}
              loading={loading}
              inputOptions={{
                id: "fromStreet",
                defaultValue: "2100 N Greenville Ave",
              }}
            />
          </div>
          <DetailPair
            label="City"
            edit={true}
            loading={loading}
            inputOptions={{
              id: "fromCity",
              defaultValue: "Richardson",
            }}
          />
          <DetailPair
            label="State"
            edit={true}
            loading={loading}
            inputOptions={{
              id: "fromState",
              defaultValue: "TX",
              maxLength: 2,
            }}
          />
          <DetailPair
            label="Zip Code"
            edit={true}
            loading={loading}
            inputOptions={{
              id: "fromZip",
              defaultValue: "75082",
              maxLength: 5,
            }}
          />
        </div>
        <p className="text-sm font-semibold text-yellow-600">
          Shipping Options
        </p>
        <div className="info">
          <SelectPair
            label="Mailing Service"
            edit={true}
            loading={loading}
            options={["Certified Mail with RR"]}
            inputOptions={{
              id: "mailingService",
            }}
          />
          <SelectPair
            label="Label Size"
            edit={true}
            loading={loading}
            options={[
              { name: "9 x 12 Window", id: "1" },
              { name: "6 x 9 Window", id: "0" },
            ]}
            inputOptions={{
              id: "labelSize",
            }}
          />
          <DetailPair
            label="Delivery Notification"
            edit={true}
            loading={loading}
            inputOptions={{
              id: "deliveryEmail",
              type: "text",
              defaultValue: admin?.email || "",
            }}
          />
        </div>
        <div className="mt-4 info">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <button onClick={confirmHandler} className="w-full btn-primary">
              Create Label
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
